/*
 * Arrows and dots for the spec-feature image carousel.
 *
 * The track itself is a CSS scroll-snap row, so swiping and trackpad scrolling already
 * work with no script at all. This only layers the buttons and the dot indicator on top —
 * if the file never loads, the carousel still scrolls, the buttons are inert and the dots
 * simply stay on the first slide. The arrows are desktop-only in CSS, which is why nothing
 * here checks a breakpoint: on a phone the track is swiped and needs no control.
 *
 * Position is read back from scrollLeft rather than held in a variable, so a swipe and a
 * button press cannot disagree about which slide is showing.
 */
(function () {
  function initCarousel(root) {
    // The section emits this script tag, so a page with two spec-feature sections executes
    // the file twice. Without this the second run would bind a duplicate set of listeners
    // to both carousels and every arrow press would advance two slides.
    if (root.dataset.specCarouselReady) return;

    var track = root.querySelector('.js-spec-feature-track');
    if (!track) return;

    root.dataset.specCarouselReady = 'true';

    var slides = track.querySelectorAll('.spec-feature__slide');
    if (slides.length < 2) return;

    var prev = root.querySelector('.js-spec-feature-prev');
    var next = root.querySelector('.js-spec-feature-next');
    var dotWrap = root.querySelector('.js-spec-feature-dots');
    var dots = dotWrap ? Array.prototype.slice.call(dotWrap.children) : [];

    function currentIndex() {
      var width = track.clientWidth;
      if (!width) return 0;
      return Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / width)));
    }

    function goTo(index) {
      var clamped = Math.min(slides.length - 1, Math.max(0, index));
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'smooth' });
    }

    function sync() {
      var index = currentIndex();
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(currentIndex() - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        goTo(currentIndex() + 1);
      });
    }

    // Coalesced to one update per frame: scroll fires far more often than the counter
    // can meaningfully change.
    var queued = false;
    track.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          sync();
        });
      },
      { passive: true }
    );

    sync();
  }

  /*
   * Opening position for the full-width carousel. Set directly rather than animated, and
   * only when the track is still at 0 — a non-zero value means the browser restored a
   * position on a back-navigation, and that belongs to the shopper, not to us.
   *
   * Mobile only, matching the breakpoint at which the CSS switches the track to centred
   * scroll-snap. That is the only width where opening on the second slide means anything:
   * it puts a peek of a neighbour on both sides. Above it the row is flush-left and
   * free-scrolling, and starting it anywhere but 0 just hides the first slide.
   *
   * Checked explicitly rather than left to the clamp below. The clamp does zero out a wide
   * desktop, where centring an early slide asks for a negative offset — but around 1024px
   * there is enough overflow for it to resolve positive and nudge the row a little, which
   * is neither wanted nor obvious.
   */
  function initAll() {
    document.querySelectorAll('.js-spec-feature-carousel').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The theme editor swaps section markup in without a page load.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      event.target.querySelectorAll('.js-spec-feature-carousel').forEach(initCarousel);
    });
  }
})();
